import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import { ApiPortalCategory, DocType } from './ApiPortalTypes';
import { ConfigHelper } from '@devxdiscover/devhub-common';
import { Progress } from '@backstage/core-components';
import { DevHubAlert, DevHubGrid, DevHubPaper } from '@devxdiscover/devhub-ui';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';
import ReactHtmlParser from 'react-html-parser';
import { DomElement } from 'htmlparser2';
import { slugifyHyphen } from '../../util/stringFormat';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

type ApiPortalDocPageProps = {
  isDataFeed?: boolean;
};

/**
 * A component to render an API Portal event or scenario doc.
 * @param props the props of the component
 * @param props.isDataFeed a boolean to determine if the page is for data feed management
 */
const ApiPortalDocPage = (props: ApiPortalDocPageProps) => {
  const navigate = useNavigate();
  const { categoryName, docType, docName } = useParams<{
    categoryName: string;
    docType: DocType;
    docName: string;
  }>();
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [docRef, setDocRef] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const { org, error, formattedOrgName } = useContext(ApiPortalContext);
  const selectedCategory = props.isDataFeed ? 'data-feed' : categoryName;
  const selectedDocType = props.isDataFeed ? DocType.Scenario : docType;

  const handleBackClick = () => {
    if (props.isDataFeed) {
      navigate(`/api-portals/${org._normalizedName}/data-feed-management`);
    } else {
      navigate(
        `/api-portals/${org._normalizedName}/${selectedCategory}/${selectedDocType}`,
      );
    }
  };

  const getDocContent = async (reference: string) => {
    const url = ConfigHelper.instance.apiUrl(reference);
    const response = await fetch(url);
    let content = await response.text();
    const imageSrcs = [
      ...new Set(
        [...content.matchAll(/<img\s+[^>]*src="([^"]+)"/g)].map(
          match => match[1],
        ),
      ),
    ];

    for (const image of imageSrcs) {
      const imageUrl = ConfigHelper.instance.apiUrl(image);
      const imageResponse = await fetch(imageUrl);
      content = content.replaceAll(
        `src="${image}"`,
        `src="${imageResponse.url}"`,
      );
    }
    setHtmlContent(content);
    setLoading(false);
  };

  const extractLastSection = (reference: string): string => {
    const lastSlashIndex = reference.lastIndexOf('/');
    const lastDotIndex = reference.lastIndexOf('.');
    if (
      lastSlashIndex === -1 ||
      lastDotIndex === -1 ||
      lastDotIndex < lastSlashIndex
    ) {
      throw new Error('Invalid reference string format');
    }
    return reference.substring(lastSlashIndex + 1, lastDotIndex);
  };

  useEffect(() => {
    let category: ApiPortalCategory | undefined;

    if (org && selectedDocType && docName && selectedCategory) {
      category = org.categoriesPaged?.categories?.find(
        x => slugifyHyphen(x.name) === selectedCategory,
      ) as ApiPortalCategory;

      if (category) {
        switch (selectedDocType) {
          case DocType.Event:
            setDocRef(
              category.apiEventsPaged?.events?.find(
                x => extractLastSection(x.reference) === docName,
              )?.reference as string,
            );
            break;
          case DocType.Scenario:
            setDocRef(
              category.apiScenariosPaged?.scenarios?.find(
                x => extractLastSection(x.reference) === docName,
              )?.reference as string,
            );
            break;
          default:
            break;
        }
      }
    }
  }, [org, selectedDocType, docName, selectedCategory]);

  useEffect(() => {
    if (docRef) {
      getDocContent(docRef);
    }
  }, [docRef]);

  const renderDoc = () => {
    const theme = localStorage.getItem('theme');
    const isDarkMode = theme === 'devhub-dark';
    const textColor = isDarkMode ? '#fff' : '#000';

    const docStyles = {
      pre: {
        language: 'javascript',
        style: materialDark,
      },
      img: {
        style: 'max-width: 80%; height: auto;',
      },
      table: {
        style: `width: 100%; border-collapse: collapse; margin: 20px 0; text-align: left; background-color: ${
          isDarkMode ? '#333' : '#fff'
        }; color: ${textColor};`,
      },
      th: {
        style: `padding: 12px 15px; border: 1px solid #ddd; background-color: ${
          isDarkMode ? '#444' : '#f4f4f4'
        }; color: ${textColor};`,
      },
      td: {
        style: `padding: 12px 15px; border: 1px solid #ddd; color: ${textColor};`,
      },
      code: {
        style: `background-color: ${
          isDarkMode ? '#444' : '#e0e0e0'
        }; border: 1px #ddd; border-radius: 3px; padding: 2px 4px; font-family: monospace; color: ${textColor};`,
      },
    };

    return (
      <DevHubGrid item xs={12}>
        <DevHubPaper
          style={{ overflowX: 'auto', maxWidth: '100%', fontSize: '1rem' }}
        >
          {/* eslint-disable-next-line new-cap */}
          {ReactHtmlParser(htmlContent, {
            transform: (node: DomElement) => {
              if (node.type === 'tag') {
                switch (node.name) {
                  case 'pre':
                    if (node.children[0].name === 'code') {
                      const language =
                        node.children[0].attribs.class?.split('-')[1] ||
                        docStyles.pre.language;
                      const code = node.children[0].children[0].data;
                      return (
                        <SyntaxHighlighter
                          language={language}
                          style={docStyles.pre.style}
                        >
                          {code}
                        </SyntaxHighlighter>
                      );
                    }
                    break;
                  case 'img':
                    node.attribs.style = docStyles.img.style;
                    break;
                  case 'table':
                    node.attribs.style = docStyles.table.style;
                    break;
                  case 'th':
                    node.attribs.style = docStyles.th.style;
                    break;
                  case 'td':
                    node.attribs.style = docStyles.td.style;
                    break;
                  case 'code':
                    node.attribs.style = docStyles.code.style;
                    break;
                  default:
                    break;
                }
              }
              return undefined;
            },
          })}
        </DevHubPaper>
      </DevHubGrid>
    );
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal docs data
      </DevHubAlert>
    );
  }

  return (
    <ApiPortalTabbedPage
      formattedOrgName={formattedOrgName}
      apiManagementContent={!props.isDataFeed ? renderDoc() : undefined}
      dataFeedContent={props.isDataFeed ? renderDoc() : undefined}
      handleBackClick={handleBackClick}
    />
  );
};

export default ApiPortalDocPage;
